import { useAtom } from 'jotai'
import { timelineContainerRefAtom } from '../atoms/timelineUi.atom'
import { allProductIdsAtom, productTimelinesAtom } from '../atoms/timeline.atom'
import { useScrollTimeline } from '../hooks/useScrollTimeline'
import { LeftLink, RightLink } from './ScheduleLinks'
import { Flex } from '@chakra-ui/react'
import {
  TIMELINE_LINK_FADE_OFFSET,
  TIMELINE_PRODUCT_ROW_HEIGHT_PX
} from '../timeline.constants'
import { useMemo } from 'react'
import { FormattedPriceSummary } from '../utils/FormattedPriceSummary'

const useProductLinks = (props: {
  productId: string
}) => {
  const { scrollTimeline } = useScrollTimeline()
  const [timelineContainerRef] = useAtom(timelineContainerRefAtom)
  const [productTimelines] = useAtom(productTimelinesAtom)
  const timelineScroll = useScrollTimeline()

  const timelineForProduct = productTimelines[props.productId]

  /**
   * Get data about the last price in the timeline - this informs
   * whether we show the left link or not.
   */
  const lastBillingPeriodForProduct = timelineForProduct?.billingPeriods.at(-1)
  const lastPriceForProduct = timelineForProduct?.prices.at(-1)
  /**
   * Get data about first price in the timeline, which informs
   * whether we show the right link or not.
   */

  const showLeftLink = useMemo(() => {
    if (!lastBillingPeriodForProduct) {
      return false
    }

    return (
      lastBillingPeriodForProduct.endOffset <
      timelineScroll.timelineScrollPosition
    )
  }, [lastBillingPeriodForProduct, timelineScroll.timelineScrollPosition])

  /**
   * Get data about first price in the timeline, which informs
   * whether we show the right link or not.
   */
  const firstBillingPeriodForProduct = timelineForProduct?.billingPeriods[0]
  const firstPriceForProduct = timelineForProduct?.prices[0]

  const currentTimelineContainerWidth = timelineContainerRef?.clientWidth ?? 0
  const showRightLink = useMemo(() => {
    if (!firstBillingPeriodForProduct) {
      return false
    }

    return (
      firstBillingPeriodForProduct.startOffset >
      timelineScroll.timelineScrollPosition + currentTimelineContainerWidth
    )
  }, [
    timelineScroll.timelineScrollPosition,
    currentTimelineContainerWidth,
    firstBillingPeriodForProduct
  ])

  return {
    left: {
      showLink: showLeftLink,
      onClick: () => {
        if (timelineContainerRef) {
          scrollTimeline({
            left:
              firstBillingPeriodForProduct.startOffset -
              TIMELINE_LINK_FADE_OFFSET
          })
        }
      },
      label: lastPriceForProduct?.name,
      summary: lastPriceForProduct?.summary
    },
    right: {
      showLink: showRightLink,
      onClick: () => {
        if (timelineContainerRef) {
          scrollTimeline({
            left:
              firstBillingPeriodForProduct.startOffset -
              TIMELINE_LINK_FADE_OFFSET
          })
        }
      },
      label: firstPriceForProduct.name ?? '',
      summary: firstPriceForProduct.summary ?? ''
    }
  }
}

const ProductLeftLink = (props: {
  productId: string
}) => {
  const {
    left: { showLink, onClick, label, summary }
  } = useProductLinks(props)
  if (!label || !summary) {
    return null
  }

  return (
    <LeftLink
      showLink={showLink}
      onClick={onClick}
      label={label}
      description={<FormattedPriceSummary {...summary} />}
    />
  )
}

const ProductRightLink = (props: {
  productId: string
}) => {
  const {
    right: { showLink, onClick, label, summary }
  } = useProductLinks(props)
  return (
    <RightLink
      showLink={showLink}
      onClick={onClick}
      label={label}
      description={<FormattedPriceSummary {...summary} />}
    />
  )
}

export const ProductLeftLinks = () => {
  const [products] = useAtom(allProductIdsAtom)

  return products.map(productId => (
    <Flex
      height={`${TIMELINE_PRODUCT_ROW_HEIGHT_PX}px`}
      key={productId}
      alignItems="flex-start"
    >
      <ProductLeftLink key={productId} productId={productId} />
    </Flex>
  ))
}

export const ProductRightLinks = () => {
  const [products] = useAtom(allProductIdsAtom)

  return products.map(productId => (
    <Flex
      height={`${TIMELINE_PRODUCT_ROW_HEIGHT_PX}px`}
      key={productId}
      justifyContent="flex-end"
      alignItems="flex-start"
    >
      <ProductRightLink productId={productId} />
    </Flex>
  ))
}
